import { Children, Fragment, type ComponentPropsWithRef, type ReactNode } from "react";
import { cn } from "../../lib/cn";

export interface StackProps extends ComponentPropsWithRef<"div"> {
  /** Direction de l'empilement : vertical (col) ou horizontal (row). Par défaut "col". */
  direction?: "col" | "row" | "col-to-row" | "row-to-col";
  /** Espacement entre les éléments (0 à 12). Par défaut 4. */
  gap?: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 8 | 10 | 12;
  /** Alignement des éléments sur l'axe secondaire (align-items). */
  align?: "start" | "center" | "end" | "stretch" | "baseline";
  /** Distribution des éléments sur l'axe principal (justify-content). */
  justify?: "start" | "center" | "end" | "between" | "around" | "evenly";
  /** Autorise les éléments à passer à la ligne suivante en mode row. */
  wrap?: boolean;
  /** Séparateur inséré automatiquement entre chaque élément. */
  divider?: ReactNode;
  children?: ReactNode;
}

const GAP_CLASSES = {
  0: "gap-0",
  1: "gap-1",
  2: "gap-2",
  3: "gap-3",
  4: "gap-4",
  5: "gap-5",
  6: "gap-6",
  8: "gap-8",
  10: "gap-10",
  12: "gap-12",
} as const;

const DIRECTION_CLASSES = {
  col: "flex-col",
  row: "flex-row",
  "col-to-row": "flex-col sm:flex-row",
  "row-to-col": "flex-row sm:flex-col",
} as const;

const ALIGN_CLASSES = {
  start: "items-start",
  center: "items-center",
  end: "items-end",
  stretch: "items-stretch",
  baseline: "items-baseline",
} as const;

const JUSTIFY_CLASSES = {
  start: "justify-start",
  center: "justify-center",
  end: "justify-end",
  between: "justify-between",
  around: "justify-around",
  evenly: "justify-evenly",
} as const;

/**
 * Composant de disposition Flexbox SOTA.
 * Gère l'alignement, la direction réactive et l'injection automatique de séparateurs.
 */
export function Stack({
  className,
  direction = "col",
  gap = 4,
  align,
  justify,
  wrap,
  divider,
  children,
  ...props
}: StackProps) {
  const validChildren = Children.toArray(children).filter(Boolean);

  return (
    <div
      className={cn(
        "flex",
        DIRECTION_CLASSES[direction],
        GAP_CLASSES[gap],
        align && ALIGN_CLASSES[align],
        justify && JUSTIFY_CLASSES[justify],
        wrap && "flex-wrap",
        className,
      )}
      {...props}
    >
      {divider
        ? validChildren.map((child, index) => (
            <Fragment key={index}>
              {child}
              {index < validChildren.length - 1 && divider}
            </Fragment>
          ))
        : children}
    </div>
  );
}

/** Empilement vertical fluide par défaut. */
export function VStack(props: Omit<StackProps, "direction">) {
  return <Stack direction="col" {...props} />;
}

/** Empilement horizontal fluide par défaut. */
export function HStack(props: Omit<StackProps, "direction">) {
  return <Stack direction="row" align="center" {...props} />;
}
